import { useState, useEffect, useCallback, useRef } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowsClockwise, Plus, Trash, CheckCircle, XCircle, Lightning, Eye, ArrowsCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

// Yieldkit admin — manages 1:1 account leases (one YK account per publisher),
// catalog full-crawl triggers, and run history. Each account assignment
// flips the publisher's `products_enabled` to include "cpc" — single
// approval gate, no other UI surface for granting Yieldkit access.
export default function AdminYieldkit() {
  const [accounts, setAccounts] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(null); // account row when open
  const [assignPubId, setAssignPubId] = useState("");
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState(false);

  // Commissions date-range sync state (per-account modal).
  const [commSyncOpen, setCommSyncOpen] = useState(null); // account row when open
  const [commStart, setCommStart] = useState(""); // YYYY-MM-DD
  const [commEnd, setCommEnd] = useState("");     // YYYY-MM-DD
  const [commSyncing, setCommSyncing] = useState(false);
  const [commResult, setCommResult] = useState(null);

  // Brand catalog browser — admin can see exactly what publishers see.
  // Same search semantics as /v1/cpc/brands (matches name starts-with OR
  // domain anywhere). Account filter lets admin scope to one publisher's
  // catalog or browse everything in db.yk_advertisers.
  const [brandRows, setBrandRows] = useState([]);
  const [brandsTotal, setBrandsTotal] = useState(0);
  const [brandsPage, setBrandsPage] = useState(1);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [brandSearch, setBrandSearch] = useState("");
  const [brandCountry, setBrandCountry] = useState("US");
  const [brandAccountId, setBrandAccountId] = useState("");
  const brandsPageSize = 25;

  // Manual GEO override dialog — opens when admin clicks the GEO chip
  // on a brand row. Used to pin a missing country code (e.g. when the
  // YK upstream didn't echo it during the country-scoped crawl).
  const [geoEditor, setGeoEditor] = useState(null);
  const [geoInput, setGeoInput] = useState("");
  const [geoSaving, setGeoSaving] = useState(false);
  const didFetch = useRef(false);

  // Create-account form
  const [form, setForm] = useState({
    label: "", api_key: "", api_secret: "", site_id: "",
    redirect_host: "", currency: "EUR",
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [accRes, pubRes, runsRes] = await Promise.all([
        API.get("/admin/yk/accounts"),
        API.get("/admin/publishers"),
        API.get("/admin/yk/sync/runs"),
      ]);
      setAccounts(accRes.data?.accounts || []);
      setPublishers(pubRes.data || []);
      setRuns(runsRes.data?.runs || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load Yieldkit data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;
    fetchAll();
  }, [fetchAll]);

  // Brand catalog fetch — separate from fetchAll so it re-runs only on
  // filter/page changes. Debounced 250ms on search input below.
  const fetchBrandCatalog = useCallback(async () => {
    setBrandsLoading(true);
    try {
      const params = {
        page: brandsPage,
        page_size: brandsPageSize,
        sort: "name",
      };
      if (brandSearch.trim()) params.search = brandSearch.trim();
      if (brandCountry) params.country = brandCountry;
      if (brandAccountId) params.account_id = brandAccountId;
      const { data } = await API.get("/admin/yk/brands", { params });
      setBrandRows(data.data || []);
      setBrandsTotal(data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load brands");
    } finally {
      setBrandsLoading(false);
    }
  }, [brandsPage, brandSearch, brandCountry, brandAccountId]);

  useEffect(() => {
    // Debounce so per-keystroke searches don't hammer Mongo.
    const t = setTimeout(fetchBrandCatalog, 250);
    return () => clearTimeout(t);
  }, [fetchBrandCatalog]);

  const handleCreate = async () => {
    if (!form.label || !form.api_key || !form.api_secret || !form.site_id) {
      toast.error("Label, API key, API secret, and Site ID are required");
      return;
    }
    setCreating(true);
    try {
      await API.post("/admin/yk/accounts", form);
      toast.success(`Created Yieldkit account: ${form.label}`);
      setCreateOpen(false);
      setForm({ label: "", api_key: "", api_secret: "", site_id: "", redirect_host: "", currency: "EUR" });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const handleTest = async (acc) => {
    const t = toast.loading(`Probing Yieldkit for "${acc.label}"...`);
    try {
      const { data } = await API.post(`/admin/yk/accounts/${acc.id}/test`);
      toast.dismiss(t);
      if (data.ok) {
        toast.success(`✓ ${acc.label}: ${data.message} (total advertisers: ${data.total})`);
      } else {
        toast.error(`✗ ${acc.label}: ${data.message}`);
      }
    } catch (err) {
      toast.dismiss(t);
      toast.error(err.response?.data?.detail || "Test failed");
    }
  };

  const handleAssign = async () => {
    if (!assignPubId || !assignOpen) return;
    setAssigning(true);
    try {
      await API.patch(`/admin/yk/accounts/${assignOpen.id}/assign`, { publisher_id: assignPubId });
      toast.success("Account assigned & Yieldkit enabled for publisher");
      setAssignOpen(null);
      setAssignPubId("");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Assign failed");
    } finally {
      setAssigning(false);
    }
  };

  const handleRevoke = async (acc) => {
    if (!window.confirm(`Revoke "${acc.label}" from ${acc.assigned_publisher_email}? This will disable Yieldkit access for that publisher.`)) return;
    try {
      await API.patch(`/admin/yk/accounts/${acc.id}/revoke`);
      toast.success("Revoked");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Revoke failed");
    }
  };

  const handleDelete = async (acc) => {
    if (acc.assigned_publisher_id) {
      toast.error("Revoke first before deleting");
      return;
    }
    if (!window.confirm(`Permanently delete account "${acc.label}" and all ${acc.advertiser_count || 0} cached advertisers?`)) return;
    try {
      await API.delete(`/admin/yk/accounts/${acc.id}`);
      toast.success("Deleted");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const handleSyncFull = async (acc) => {
    if (!window.confirm(`Trigger full catalog crawl for "${acc.label}"? This walks every country (US, GB, DE, FR, ...) and may take a few minutes.`)) return;
    try {
      await API.post(`/admin/yk/accounts/${acc.id}/sync/full`, {});
      toast.success("Full sync queued — refresh the runs panel below to track progress");
      setTimeout(fetchAll, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    }
  };

  // Open the commissions date-range sync modal pre-filled with the
  // last 7 days — same default the scheduler uses, but visible to
  // the admin so they can adjust before firing.
  const openCommSync = (acc) => {
    const end = new Date();
    const start = new Date(end);
    start.setDate(start.getDate() - 7);
    const ymd = (d) => d.toISOString().slice(0, 10);
    setCommStart(ymd(start));
    setCommEnd(ymd(end));
    setCommResult(null);
    setCommSyncOpen(acc);
  };

  const handleCommSync = async () => {
    if (!commSyncOpen) return;
    if (!commStart || !commEnd) {
      toast.error("Pick both start and end dates"); return;
    }
    if (commStart > commEnd) {
      toast.error("Start date must be on or before end date"); return;
    }
    setCommSyncing(true);
    setCommResult(null);
    try {
      const { data } = await API.post(
        `/admin/yk/accounts/${commSyncOpen.id}/sync/commissions`,
        { start_date: commStart, end_date: commEnd },
      );
      setCommResult(data);
      const r = (data?.results || [])[0] || {};
      const sales = r.sales?.rows || 0;
      const modified = r.modified?.rows || 0;
      toast.success(
        `Synced ${sales + modified} commission rows (${sales} sales / ${modified} modified)`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Commissions sync failed");
    } finally {
      setCommSyncing(false);
    }
  };

  return (
    <div className="p-8 space-y-8" data-testid="admin-yieldkit-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 font-bold tracking-[0.2em] uppercase">NETWORK</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Yieldkit</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage 1:1 account leases. Each account belongs to exactly one publisher; assigning auto-enables Yieldkit for them.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchAll} data-testid="yk-refresh-btn">
            <ArrowsClockwise size={14} className="mr-1.5" /> Refresh
          </Button>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#002FA7] hover:bg-[#002FA7]/90 text-white" data-testid="yk-create-btn">
                <Plus size={14} className="mr-1.5" /> Add Account
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle>Add Yieldkit Account</DialogTitle>
                <DialogDescription>
                  Create a new Yieldkit lease. Assign it to a publisher to enable Yieldkit access for them.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-2">
                <div className="grid grid-cols-1 gap-1.5">
                  <Label htmlFor="yk-label">Label</Label>
                  <Input id="yk-label" placeholder="e.g. Yieldkit-Omar"
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    data-testid="yk-input-label" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-1.5">
                    <Label htmlFor="yk-key">API Key</Label>
                    <Input id="yk-key" placeholder="api_key from Yieldkit"
                      value={form.api_key}
                      onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                      data-testid="yk-input-key" />
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="yk-secret">API Secret</Label>
                    <Input id="yk-secret" type="password" placeholder="api_secret"
                      value={form.api_secret}
                      onChange={(e) => setForm({ ...form, api_secret: e.target.value })}
                      data-testid="yk-input-secret" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-1.5">
                    <Label htmlFor="yk-site">Site ID</Label>
                    <Input id="yk-site" placeholder="site_id"
                      value={form.site_id}
                      onChange={(e) => setForm({ ...form, site_id: e.target.value })}
                      data-testid="yk-input-site" />
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="yk-redirect">Redirect Host (optional)</Label>
                    <Input id="yk-redirect" placeholder="r.linksprf.com"
                      value={form.redirect_host}
                      onChange={(e) => setForm({ ...form, redirect_host: e.target.value })}
                      data-testid="yk-input-redirect" />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
                <Button onClick={handleCreate} disabled={creating} className="bg-[#002FA7] hover:bg-[#002FA7]/90" data-testid="yk-create-confirm-btn">
                  {creating ? "Creating…" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Accounts table */}
      <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Label</th>
              <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Site ID</th>
              <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">API Key</th>
              <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Assigned to</th>
              <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Advertisers</th>
              <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Last Sync</th>
              <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400 text-sm">Loading…</td></tr>
            )}
            {!loading && accounts.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-slate-500 text-sm">
                  No Yieldkit accounts yet. Click <strong>Add Account</strong> to onboard your first one.
                </td>
              </tr>
            )}
            {accounts.map((acc) => (
              <tr key={acc.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`yk-row-${acc.id}`}>
                <td className="px-4 py-3 font-semibold text-slate-900">
                  <div className="flex items-center gap-2">
                    <Lightning size={14} className="text-[#002FA7]" weight="fill" />
                    {acc.label}
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-[11px] text-slate-600">{acc.site_id}</td>
                <td className="px-4 py-3 font-mono text-[11px] text-slate-500">
                  {acc.api_key?.slice(0, 6)}…<span className="text-slate-400">·····</span>{acc.api_secret_last4}
                </td>
                <td className="px-4 py-3">
                  {acc.assigned_publisher_email ? (
                    <span className="inline-flex items-center gap-1.5 text-emerald-700 text-xs font-semibold">
                      <CheckCircle size={12} weight="fill" /> {acc.assigned_publisher_email}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-amber-700 text-xs font-semibold">
                      <XCircle size={12} weight="fill" /> Unassigned
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right font-mono text-[11px]">
                  {(acc.advertiser_count || 0).toLocaleString()}
                </td>
                <td className="px-4 py-3 text-[11px] text-slate-500">
                  {acc.last_full_sync_at ? new Date(acc.last_full_sync_at).toLocaleString() : "Never"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap justify-end gap-1.5">
                    <Button variant="outline" size="sm" className="h-7 text-[11px] px-2" onClick={() => handleTest(acc)} title="Live-probe Yieldkit with this lease's keys" data-testid={`yk-test-${acc.id}`}>
                      <Eye size={12} className="mr-1" /> Test
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 text-[11px] px-2" onClick={() => handleSyncFull(acc)} title="Crawl every country" data-testid={`yk-sync-${acc.id}`}>
                      <ArrowsCounterClockwise size={12} className="mr-1" /> Sync
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 text-[11px] px-2 text-indigo-700 border-indigo-300 hover:bg-indigo-50" onClick={() => openCommSync(acc)} title="Fetch commissions for a date range" data-testid={`yk-sync-comm-${acc.id}`}>
                      <ArrowsClockwise size={12} className="mr-1" /> Sync Report
                    </Button>
                    {acc.assigned_publisher_id ? (
                      <Button variant="outline" size="sm" className="h-7 text-[11px] px-2 text-amber-700 border-amber-300 hover:bg-amber-50" onClick={() => handleRevoke(acc)} data-testid={`yk-revoke-${acc.id}`}>
                        Revoke
                      </Button>
                    ) : (
                      <Button size="sm" className="h-7 text-[11px] px-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => { setAssignOpen(acc); setAssignPubId(""); }} data-testid={`yk-assign-${acc.id}`}>
                        Assign
                      </Button>
                    )}
                    <Button variant="outline" size="sm" className="h-7 px-2 text-rose-700 border-rose-300 hover:bg-rose-50" onClick={() => handleDelete(acc)} data-testid={`yk-delete-${acc.id}`}>
                      <Trash size={12} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Brand catalog — same data the publishers see, but admin-scoped
          and searchable across any account. Useful for spot-checking
          "is this brand in Kevin's catalog?" without logging in as them. */}
      <div data-testid="admin-yk-brand-catalog">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-heading text-base font-bold text-slate-900">Brand Catalog</h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Search the live <code className="bg-slate-100 px-1 rounded">yk_advertisers</code> mirror. Matches{" "}
              <span className="font-mono">name</span> (starts-with) OR <span className="font-mono">domain</span> (anywhere).
            </p>
          </div>
          <span className="text-[10px] text-slate-400 uppercase tracking-[0.2em] font-bold">
            {brandsTotal.toLocaleString()} brand{brandsTotal === 1 ? "" : "s"}
          </span>
        </div>

        {/* Filters row */}
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="admin-yk-brand-filters">
          <Input
            value={brandSearch}
            onChange={(e) => { setBrandSearch(e.target.value); setBrandsPage(1); }}
            placeholder="Search by name or domain (e.g. enjoymiss or .com)"
            className="h-9 text-sm w-72"
            data-testid="admin-yk-brand-search"
          />
          <Select value={brandCountry || "ALL"} onValueChange={(v) => { setBrandCountry(v === "ALL" ? "" : v); setBrandsPage(1); }}>
            <SelectTrigger className="h-9 w-32 text-sm" data-testid="admin-yk-brand-country">
              <SelectValue placeholder="Country" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All countries</SelectItem>
              {["US","GB","DE","FR","IT","ES","NL","BE","AT","CH","PL","SE","NO","DK","FI","IE","PT","AU","CA","JP"].map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={brandAccountId || "ALL"} onValueChange={(v) => { setBrandAccountId(v === "ALL" ? "" : v); setBrandsPage(1); }}>
            <SelectTrigger className="h-9 w-48 text-sm" data-testid="admin-yk-brand-account">
              <SelectValue placeholder="Account" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All accounts</SelectItem>
              {accounts.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.label || a.id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="ml-auto flex items-center gap-1.5">
            <Button
              variant="outline" size="sm"
              disabled={brandsPage <= 1 || brandsLoading}
              onClick={() => setBrandsPage(brandsPage - 1)}
              data-testid="admin-yk-brand-prev"
            >Prev</Button>
            <span className="text-[11px] text-slate-500 px-2">
              Page <span className="font-mono font-semibold">{brandsPage}</span> /{" "}
              <span className="font-mono">{Math.max(1, Math.ceil(brandsTotal / brandsPageSize))}</span>
            </span>
            <Button
              variant="outline" size="sm"
              disabled={brandsLoading || brandsPage * brandsPageSize >= brandsTotal}
              onClick={() => setBrandsPage(brandsPage + 1)}
              data-testid="admin-yk-brand-next"
            >Next</Button>
          </div>
        </div>

        {/* Brand table */}
        <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Advertiser</th>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Domain</th>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Country</th>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Category</th>
                <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
                  {brandCountry ? `Commission (${brandCountry})` : "Best Commission"}
                </th>
                <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">eCPC</th>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Account</th>
                <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Geo</th>
              </tr>
            </thead>
            <tbody>
              {brandsLoading && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400 text-sm">Loading…</td></tr>
              )}
              {!brandsLoading && brandRows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-slate-500 text-sm">
                    {brandSearch.trim() ? (
                      <>No brands match <span className="font-mono">"{brandSearch}"</span>{brandCountry ? ` in ${brandCountry}` : ""}. Try a broader query.</>
                    ) : (
                      <>No brands in catalog yet — run a full sync to populate.</>
                    )}
                  </td>
                </tr>
              )}
              {brandRows.map((b) => {
                const acc = accounts.find((a) => a.id === b.account_id);
                // Per-country commission lookup. Same pattern as PubCPC —
                // when a specific country is filtered, show that country's
                // commission + currency from country_metrics; otherwise pick
                // the best-paying country across the metrics map.
                const cm = b.country_metrics || {};
                let metric = brandCountry ? cm[brandCountry] : null;
                let pickedCountry = brandCountry;
                if (!metric) {
                  let best = null, bestKey = null;
                  for (const [k, v] of Object.entries(cm)) {
                    const c = Number(v?.commission || 0);
                    if (best === null || c > Number(best.commission || 0)) {
                      best = v; bestKey = k;
                    }
                  }
                  metric = best;
                  pickedCountry = bestKey;
                }
                const commission = Number(metric?.commission || 0);
                const metricCurrency = metric?.currency || "EUR";
                return (
                  <tr key={`${b.account_id}-${b.advertiser_id}`} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-yk-brand-row-${b.advertiser_id}`}>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        {b.image ? (
                          <img src={b.image} alt={b.name || "advertiser"} className="w-6 h-6 rounded object-cover bg-slate-100" />
                        ) : (
                          <div className="w-6 h-6 rounded bg-slate-100" />
                        )}
                        <span className="text-sm font-medium text-slate-800">{b.name || "—"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-[11px] font-mono text-slate-600">
                      {b.domain ? (
                        <a href={b.url || `https://${b.domain}`} target="_blank" rel="noreferrer" className="hover:underline text-[#002FA7]">{b.domain}</a>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-2 text-[11px] text-slate-600">{b.country || "—"}</td>
                    <td className="px-4 py-2 text-[11px] text-slate-600">{b.category || "—"}</td>
                    <td className="px-4 py-2 text-[11px] font-mono text-right" data-testid={`admin-yk-commission-${b.advertiser_id}`}>
                      {commission > 0 ? (
                        <span className="text-[#002FA7] font-bold">
                          {metricCurrency === "EUR" ? "€" : metricCurrency === "USD" ? "$" : ""}
                          {commission.toFixed(2)}
                          {!brandCountry && pickedCountry && (
                            <span className="text-emerald-700 text-[9px] uppercase tracking-wider ml-1 font-bold">{pickedCountry}</span>
                          )}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-2 text-[11px] font-mono text-right text-slate-700">
                      {b.ecpc ? `€${Number(b.ecpc).toFixed(3)}` : "—"}
                    </td>
                    <td className="px-4 py-2 text-[11px] text-slate-500">{acc?.label || b.account_id?.slice(0, 8) || "—"}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => setGeoEditor({ ...b, _account: acc?.label || b.account_id })}
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 hover:text-[#002FA7] px-2 py-1 rounded border border-slate-200 hover:border-[#002FA7]"
                        title="Add/override GEO coverage for this brand"
                        data-testid={`admin-yk-brand-geo-edit-${b.advertiser_id}`}
                      >
                        {(b.countries && b.countries.length > 0)
                          ? `${b.countries.slice(0, 3).join(", ")}${b.countries.length > 3 ? "…" : ""}`
                          : "Add GEO"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent sync runs */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-base font-bold text-slate-900">Recent Sync Runs</h2>
          <span className="text-[10px] text-slate-400 uppercase tracking-[0.2em] font-bold">Last 30</span>
        </div>
        <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Started</th>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Account</th>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Stage</th>
                <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Pages</th>
                <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Advertisers</th>
                <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Countries</th>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Status</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400 text-sm">No sync runs yet</td></tr>
              )}
              {runs.map((r) => (
                <tr key={r.id} className="border-b border-slate-100">
                  <td className="px-4 py-2 text-[11px] text-slate-600">{r.started_at ? new Date(r.started_at).toLocaleString() : "-"}</td>
                  <td className="px-4 py-2 text-[11px] font-semibold text-slate-700">{r.account_label || "-"}</td>
                  <td className="px-4 py-2 text-[11px] text-slate-600">{r.stage || "-"}</td>
                  <td className="px-4 py-2 text-[11px] font-mono text-right">{r.pages_fetched || 0}</td>
                  <td className="px-4 py-2 text-[11px] font-mono text-right">{(r.advertisers_so_far || 0).toLocaleString()}</td>
                  <td className="px-4 py-2 text-[11px] font-mono text-right">{r.countries_done || 0}/{(r.countries || []).length}</td>
                  <td className="px-4 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                      r.status === "completed" ? "bg-emerald-100 text-emerald-700"
                      : r.status === "failed" ? "bg-rose-100 text-rose-700"
                      : "bg-slate-100 text-slate-600"
                    }`}>{r.status || "running"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assign modal */}
      <Dialog open={!!assignOpen} onOpenChange={(o) => { if (!o) setAssignOpen(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign "{assignOpen?.label}"</DialogTitle>
            <DialogDescription>
              Pick a publisher. Assigning will <strong>auto-enable Yieldkit</strong> for them — they'll see Yieldkit brands in their portal and via <code className="bg-slate-100 px-1 rounded">/v1/cpc/brands</code>.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <Label>Publisher</Label>
              <Select value={assignPubId} onValueChange={setAssignPubId}>
                <SelectTrigger data-testid="yk-assign-pub-select"><SelectValue placeholder="Select a publisher…" /></SelectTrigger>
                <SelectContent>
                  {publishers
                    .filter((p) => (p.status || "active") === "active")
                    .map((p) => (
                      <SelectItem key={p.id} value={p.sub_id || p.id}>
                        {p.email} {p.first_name ? `· ${p.first_name} ${p.last_name || ""}` : ""}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Each Yieldkit account is bound to a single publisher. They cannot be shared.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(null)} disabled={assigning}>Cancel</Button>
            <Button onClick={handleAssign} disabled={assigning || !assignPubId} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="yk-assign-confirm-btn">
              {assigning ? "Assigning…" : "Assign & Enable Yieldkit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Commissions date-range sync dialog — fetches /sales + /modified
          for the chosen window. Surfaces per-row counts on success. */}
      <Dialog
        open={!!commSyncOpen}
        onOpenChange={(o) => { if (!o) { setCommSyncOpen(null); setCommResult(null); } }}
      >
        <DialogContent className="max-w-md" data-testid="yk-comm-sync-dialog">
          <DialogHeader>
            <DialogTitle>Sync commissions — &quot;{commSyncOpen?.label}&quot;</DialogTitle>
            <DialogDescription>
              Pulls <span className="font-mono">/reports/commissions/sales</span> +{" "}
              <span className="font-mono">/modified</span> for the chosen window.
              Idempotent — re-running over the same range is a no-op on
              already-ingested events (status transitions are picked up).
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Start date</Label>
              <Input
                type="date"
                value={commStart}
                onChange={(e) => setCommStart(e.target.value)}
                data-testid="yk-comm-start"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">End date</Label>
              <Input
                type="date"
                value={commEnd}
                onChange={(e) => setCommEnd(e.target.value)}
                data-testid="yk-comm-end"
              />
            </div>
          </div>
          {commResult ? (
            <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs space-y-1">
              {(commResult.results || []).map((r, i) => (
                <div key={i} className="space-y-0.5">
                  <div className="font-bold text-slate-700">{r.label}</div>
                  {r.ok ? (
                    <div className="text-slate-600">
                      Sales: <span className="font-mono">{r.sales?.rows ?? 0}</span> rows / <span className="font-mono">{r.sales?.upserted ?? 0}</span> upserted /{" "}
                      <span className="font-mono">{r.sales?.fanouts ?? 0}</span> fan-outs <br />
                      Modified: <span className="font-mono">{r.modified?.rows ?? 0}</span> rows / <span className="font-mono">{r.modified?.upserted ?? 0}</span> upserted /{" "}
                      <span className="font-mono">{r.modified?.fanouts ?? 0}</span> fan-outs
                      {r.debug && ((r.sales?.rows ?? 0) === 0 || (r.modified?.rows ?? 0) === 0) ? (
                        <pre className="mt-1 text-[10px] bg-white border border-slate-200 rounded p-2 overflow-x-auto whitespace-pre-wrap" data-testid="yk-comm-sync-debug">
                          {JSON.stringify(r.debug, null, 2)}
                        </pre>
                      ) : null}
                    </div>
                  ) : (
                    <div className="text-rose-700">Failed: {r.error}</div>
                  )}
                </div>
              ))}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCommSyncOpen(null); setCommResult(null); }} disabled={commSyncing}>
              Close
            </Button>
            <Button onClick={handleCommSync} disabled={commSyncing} data-testid="yk-comm-sync-fire">
              {commSyncing ? "Syncing..." : "Sync now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual GEO override dialog (Brand Catalog → row → GEO chip) */}
      <Dialog open={!!geoEditor} onOpenChange={(o) => { if (!o) { setGeoEditor(null); setGeoInput(""); } }}>
        <DialogContent className="max-w-md" data-testid="yk-geo-editor-dialog">
          <DialogHeader>
            <DialogTitle>Set GEO for "{geoEditor?.name || geoEditor?.domain}"</DialogTitle>
            <DialogDescription>
              The Yieldkit upstream sometimes omits country tags on individual brand rows. Add ISO-2 codes (comma-separated, e.g. <span className="font-mono">US, GB, DE</span>) to pin coverage. Values merge with any existing GEO.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Current</Label>
              <div className="mt-1 text-[11px] font-mono text-slate-700">
                {(geoEditor?.countries && geoEditor.countries.length > 0)
                  ? geoEditor.countries.join(", ")
                  : <span className="text-slate-400">— none —</span>}
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="yk-geo-input" className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
                Add countries
              </Label>
              <Input
                id="yk-geo-input"
                placeholder="US, GB, DE"
                value={geoInput}
                onChange={(e) => setGeoInput(e.target.value.toUpperCase())}
                className="font-mono"
                data-testid="yk-geo-input"
              />
              <p className="text-[10px] text-slate-400">
                Comma-separated ISO-2 codes. They'll be added via $addToSet — no duplicates.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setGeoEditor(null); setGeoInput(""); }} disabled={geoSaving}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                const codes = geoInput.split(/[\s,]+/).map((c) => c.trim().toUpperCase()).filter(Boolean);
                if (codes.length === 0) {
                  toast.error("Enter at least one country code");
                  return;
                }
                setGeoSaving(true);
                try {
                  await API.patch(
                    `/admin/yk/brands/${geoEditor.advertiser_id}/geo`,
                    { countries: codes },
                    { params: { account_id: geoEditor.account_id } },
                  );
                  toast.success(`Added ${codes.join(", ")} to "${geoEditor.name || geoEditor.domain}"`);
                  setGeoEditor(null);
                  setGeoInput("");
                  fetchBrandCatalog();
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Failed to update GEO");
                } finally {
                  setGeoSaving(false);
                }
              }}
              disabled={geoSaving || !geoInput.trim()}
              className="bg-[#002FA7] hover:bg-[#002089] text-white"
              data-testid="yk-geo-save"
            >
              {geoSaving ? "Saving…" : "Save GEO"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
